import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { HomeFeedItem } from './homeTypes';
import apiService from '../../services/ApiService';

const VARIANT_ACCENT: Record<string, string> = {
  urgent: 'border-l-orange-500',
  time_sensitive: 'border-l-amber-500',
  upcoming: 'border-l-sky-500',
  passport: 'border-l-emerald-500',
  build: 'border-l-zinc-400',
  event: 'border-l-sky-500',
  club: 'border-l-violet-400',
  shop: 'border-l-teal-400',
  organizer: 'border-l-orange-400',
  judge: 'border-l-rose-400',
  system: 'border-l-zinc-500',
  watched: 'border-l-zinc-500',
  completed: 'border-l-emerald-600',
};

function categoryLabel(item: HomeFeedItem): string {
  if (item.status_label) return item.status_label;
  const cat = (item.category || item.variant || 'update').replace(/_/g, ' ');
  return cat.charAt(0).toUpperCase() + cat.slice(1);
}

interface HomeFeedCardProps {
  item: HomeFeedItem;
  onDismiss?: (item: HomeFeedItem) => void;
}

export const HomeFeedCard: React.FC<HomeFeedCardProps> = ({ item, onDismiss }) => {
  const navigate = useNavigate();
  const accent = VARIANT_ACCENT[item.variant || ''] || VARIANT_ACCENT[item.category || ''] || 'border-l-zinc-600';
  const href = item.primary_action?.web_url || item.web_url || item.deep_link || null;
  const isUnread = item.read_state !== 'read' || item.action_required;

  const open = () => {
    void apiService.trackEvent('home_feed_item_opened', {
      metadata: { item_key: item.item_key, type: item.type, category: item.category },
    });
    if (item.read_state !== 'read') {
      void apiService.setHomeFeedState('read', item.item_key).catch(() => undefined);
    }
    if (href) {
      navigate(href);
    }
  };

  const label = [
    categoryLabel(item),
    item.headline,
    item.time_label || '',
    item.action_required ? 'Action required' : '',
  ]
    .filter(Boolean)
    .join('. ');

  return (
    <article
      className={`group relative border-l-2 ${accent} rounded-r-lg border border-white/[0.06] bg-[#0e0e0e]/90 px-3.5 py-3 sm:px-4 sm:py-3.5 transition-colors hover:border-white/10 ${
        isUnread ? '' : 'opacity-80'
      }`}
      aria-label={label}
    >
      <div className="flex gap-3">
        {item.image_url && apiService.getFileUrl(item.image_url) ? (
          <img
            src={apiService.getFileUrl(item.image_url) as string}
            alt=""
            className="h-12 w-12 shrink-0 rounded-md object-cover bg-zinc-900"
          />
        ) : null}
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <p className="spec-label text-[11px] tracking-wide text-zinc-500">
              {categoryLabel(item)}
              {item.time_label ? <span className="text-zinc-600"> · {item.time_label}</span> : null}
            </p>
            {item.action_required ? (
              <span className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-orange-300 bg-orange-500/10 border border-orange-500/20">
                Action
              </span>
            ) : null}
          </div>
          <h3 className="mt-1 font-display text-[15px] sm:text-base font-medium text-white leading-snug text-balance">
            {item.headline}
          </h3>
          {item.body ? (
            <p className="mt-1 text-sm text-zinc-400 leading-relaxed line-clamp-2">{item.body}</p>
          ) : null}
          {item.progress ? (
            <div className="mt-2.5" aria-label={`Progress ${item.progress.label || `${item.progress.percent}%`}`}>
              <div className="flex justify-between text-[11px] text-zinc-500 mb-1">
                <span>Progress</span>
                <span>{item.progress.label || `${Math.round(item.progress.percent)}%`}</span>
              </div>
              <div className="h-1 rounded-full bg-white/[0.06] overflow-hidden">
                <div
                  className="h-full rounded-full bg-orange-500/80 transition-[width] duration-500"
                  style={{ width: `${Math.min(100, Math.max(0, item.progress.percent))}%` }}
                />
              </div>
            </div>
          ) : null}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {href ? (
              <button
                type="button"
                onClick={open}
                className="min-h-[40px] px-3 rounded-md bg-white/[0.08] hover:bg-white/[0.12] text-sm text-white font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400"
              >
                {item.primary_action?.label || 'Open'}
              </button>
            ) : null}
            {item.secondary_action?.web_url ? (
              <button
                type="button"
                onClick={() => {
                  void apiService.trackEvent('home_feed_action_completed', {
                    metadata: { item_key: item.item_key, action: 'secondary' },
                  });
                  navigate(item.secondary_action!.web_url!);
                }}
                className="min-h-[40px] px-3 rounded-md text-sm text-zinc-400 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400"
              >
                {item.secondary_action.label}
              </button>
            ) : null}
            {item.dismissible && onDismiss ? (
              <button
                type="button"
                onClick={() => onDismiss(item)}
                className="ml-auto min-h-[40px] px-2 text-xs text-zinc-500 hover:text-zinc-300 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400"
                aria-label={`Dismiss ${item.headline}`}
              >
                Dismiss
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </article>
  );
};

export default HomeFeedCard;
